import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Sparkles, LockKeyhole, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

// Owner spec 2026-02-08 — PIN gate. The email/password form + Google
// signin + register link were removed at the owner's explicit request:
// "רק עם קוד 1234 זהו, בלי איימיל וכו". The whole app is now behind a
// single PIN read from backend env `APP_PIN`. Correct PIN → the
// backend issues a super_admin token and we redirect straight in.
export default function LoginPage() {
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/pin-login", { pin });
      if (!data?.token || !data?.user) throw new Error("bad response");
      if (setSession) setSession(data.user, data.token, true);
      const next = location.state?.from?.pathname;
      // Any authenticated user lands on Full Frame directly — that's
      // the entire product surface the owner cares about right now.
      navigate(next || "/full-frame", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "קוד שגוי";
      setError(msg);
      setPin("");
      inputRef.current?.focus();
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onChange = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 6);
    setPin(digits);
    if (error) setError("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-bl from-brand-50 via-white to-stone-50" dir="rtl">
      <Toaster position="top-center" richColors dir="rtl" />
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-8 space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold">
              <Sparkles className="h-3 w-3" />
              FlowUp
            </div>
            <div className="flex items-center justify-center pt-2">
              <div className="h-14 w-14 rounded-full bg-brand/10 text-brand flex items-center justify-center">
                <LockKeyhole className="h-7 w-7" />
              </div>
            </div>
            <h1 className="font-heading text-2xl font-bold">הזן קוד כניסה</h1>
            <p className="text-xs text-stone-500">כניסה למערכת FlowUp</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="pin-login-form">
            <div>
              <input
                ref={inputRef}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                value={pin}
                onChange={onChange}
                placeholder="••••"
                className={`w-full text-center text-3xl tracking-[0.6em] font-bold py-4 rounded-lg border-2 outline-none transition ${
                  error
                    ? "border-red-400 bg-red-50 text-red-700"
                    : "border-stone-300 bg-stone-50 focus:border-brand focus:bg-white"
                }`}
                maxLength={6}
                data-testid="pin-login-input"
                aria-label="קוד כניסה"
              />
              {error && (
                <div className="text-red-600 text-xs font-bold text-center mt-2" data-testid="pin-login-error">
                  {error}
                </div>
              )}
            </div>
            <button
              type="submit"
              disabled={busy || pin.length < 4}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-extrabold text-sm rounded-lg px-4 py-3 inline-flex items-center justify-center gap-2 transition"
              data-testid="pin-login-submit"
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> מאמת...</>
              ) : (
                <>כניסה</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
